<template>
  <div v-show="showContent">
    <form-cover-image
      @first-btn-action="firstBtnHandler"
      @second-btn-action="secondBtnHandler"
      :backgroundImage="coverBackground.sourceUrl"
      :style="coverImgStyle"
      :content="coverImgContent"
      :buttons="coverImgButtons"
      :showSpinner="showSpinner"
    >
    </form-cover-image>
    <div class="container">
      <transition name="project">
        <div v-show="actions.addProject" class="project-container">
          <project-form @form-submitted="saveProjectForm"></project-form>
        </div>
      </transition>
      <transition name="images">
        <div ref="content" v-show="actions.addImages" class="images-container">
          <FileUpload
            :multiple="true"
            :customUpload="true"
            :showUploadButton="true"
            @uploader="uploadImages"
            @clear="clearUpload"
            @select="imagesSelected"
          />
        </div>
      </transition>
      <transition name="client">
        <div v-show="actions.addClient" class="client-container">
          <client-form @form-submitted="saveClientForm"></client-form>
        </div>
      </transition>
      <transition name="change-cover">
        <div v-show="actions.changeCoverImage" class="change-cover-container">
          <items-grid
            :items="imageUrls"
            type="images"
            :multiple="false"
            @item-clicked="setCoverBackground"
            @selected="saveCustomCover"
          ></items-grid>
        </div>
      </transition>
    </div>
  </div>
  <loading-spinner
    v-if="showSpinner && steps.firstLoad === 0"
    mode="full"
  ></loading-spinner>
</template>

<script>
import { ref, reactive, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import useFormStylingLogic from "../composables/form-styling.js";

import FormCoverImage from "../components/FormCoverImage.vue";
import ProjectForm from "../components/forms/ProjectForm.vue";
import ClientForm from "../components/forms/ClientForm.vue";
import ItemsGrid from "../components/ItemsGrid.vue";

import FileUpload from "primevue/fileupload";
export default {
  components: {
    FormCoverImage,
    ProjectForm,
    ClientForm,
    FileUpload,
    ItemsGrid
  },
  setup() {
    const showContent = ref(false);
    const store = useStore();
    const router = useRouter();
    const content = ref(null);
    const showSpinner = ref(true);
    const {
      coverImgButtons,
      setCoverImgButtons,
      coverImgStyle,
      setCoverImgStyle,
      coverImgContent,
      setCoverImgContent,
      actions,
      addImages,
      addClient,
      imagesChosen,
      imagesCleared,
      changeCoverImage,
      changeContentStyle
    } = useFormStylingLogic();

    const steps = reactive({
      firstLoad: 0,
      projectInfo: 0,
      projectImages: 0,
      clientInfo: 0,
      customCoverImg: 0
    });

    /* DEFAULT COVER IMAGE & CONTENT  */
    const coverBackground = reactive({
      downloadUrl: null,
      sourceUrl: null
    });
    const defaultCoverImage = ref(null);
    async function getDefaultCoverImage() {
      defaultCoverImage.value = await store.dispatch("getDefaultCoverImage");
      setCoverBackground({ url: defaultCoverImage.value });
      showSpinner.value = false;
      showContent.value = true;
    }
    getDefaultCoverImage();
    setCoverImgButtons("Add Images", "Skip");

    /* PROJECT FORM */
    const savedProject = reactive({});
    const savedProjectId = ref("");
    async function saveProjectForm(project) {
      steps.firstLoad = 1;
      showSpinner.value = true;
      savedProjectId.value = await store.dispatch("saveProject", project);
      savedProject.value = project;
      afterProjectSaved();
      showSpinner.value = false;
    }
    async function afterProjectSaved() {
      actions.addProject = false;
      steps.projectInfo = 1;
      setCoverImgStyle("large", "full", "opaque");
      setCoverImgContent(
        savedProject.value.name,
        "Project information saved!",
        "pi-check-circle"
      );
    }

    /* PROJECT IMAGES */
    function imagesSelected() {
      content.value.classList.add("images-chosen");
      imagesChosen();
    }

    function clearUpload() {
      content.value.classList.remove("images-chosen");
      const mobile = checkMobile();
      imagesCleared(mobile);
    }
    async function uploadImages(event) {
      showSpinner.value = true;
      for (const file of event.files) {
        await store.dispatch("uploadImage", {
          path: savedProjectId.value,
          image: file
        });
      }
      afterImagesUploaded();
      showSpinner.value = false;
    }
    function afterImagesUploaded(skipped = false) {
      actions.addImages = false;
      steps.projectImages = 1;
      let message = "Images uploaded successfully";
      if (skipped) {
        message = "No images added";
        actions.addProject = false;
        changeContentStyle(content, "0vh", "0 2rem");
      }
      actions.imagesChosen = false;
      setCoverImgStyle("large", "full");
      setCoverImgContent(null, message, "pi-check-circle");
      setCoverImgButtons("Add Client", "Skip");
    }

    /* CLIENT FORM */
    async function saveClientForm(client) {
      showSpinner.value = true;
      const clientId = await store.dispatch("saveClient", client);
      savedProject.value.clientId = clientId;
      await store.dispatch("updateProject", {
        updateData: savedProject.value,
        projectId: savedProjectId.value
      });
      afterClientInfoSaved();
      showSpinner.value = false;
    }
    function afterClientInfoSaved(skipped = false) {
      actions.addClient = false;
      let message = "Client has been successfully added!";
      if (skipped) {
        message = "No client added";
        changeContentStyle(content, "0vh", "0 2rem");
        actions.addImages = false;
      }
      steps.clientInfo = 1;
      setCoverImgStyle("large", "full");
      setCoverImgContent(savedProject.value.name, message, "pi-check-circle");
      setCoverImgButtons("Change Cover Image", "Done");
    }

    /* CHANGING COVER IMAGE */
    const imageUrls = ref([]);
    async function listProjectImages() {
      const result = await store.dispatch(
        "listProjectImages",
        savedProjectId.value
      );
      return result.items;
    }
    async function getProjectImages() {
      showSpinner.value = true;
      const imagesRefsList = await listProjectImages();
      for (const imgRef of imagesRefsList) {
        const downloadUrl = await store.dispatch(
          "getImgDownloadUrl",
          imgRef.fullPath
        );
        imageUrls.value.push({ url: downloadUrl, alt: "Project image" });
      }
      const mobile = checkMobile();
      changeCoverImage(mobile);
      showSpinner.value = false;
    }
    async function deleteOldCustomCover() {
      const path = `${savedProjectId.value}/custom_cover`;
      const oldImageRef = await store.dispatch("listProjectImages", path);
      if (oldImageRef.items.length) {
        const fullPath = `${path}/${oldImageRef.items[0].name}`;
        return await store.dispatch("deleteStorageItem", fullPath);
      } else return;
    }
    async function saveCustomCover(file) {
      showSpinner.value = true;
      await deleteOldCustomCover();
      const result = await store.dispatch("uploadImage", {
        path: `${savedProjectId.value}/custom_cover`,
        image: file
      });
      const downloadUrl = await store.dispatch(
        "getImgDownloadUrl",
        result.ref.fullPath
      );
      setCoverBackground({ url: downloadUrl });
      showSpinner.value = false;
    }
    function setCoverBackground(selected) {
      const selectedImage = selected.item ? selected.item : selected;
      coverBackground.downloadUrl = selectedImage.url;
      coverBackground.sourceUrl = `url("${selectedImage.url}")`;
    }

    /* COVER IMAGE BUTTONS HANDLING */
    async function firstBtnHandler() {
      switch (coverImgButtons.firstBtn) {
        case "Add Images":
          if (checkMobile()) addImages(true);
          else addImages(false);
          break;
        case "Add Client":
          changeContentStyle(content, "60vh", "2rem");
          addClient();
          break;
        case "Change Cover Image":
          await getProjectImages();
          break;
        case "Back to default image":
          await deleteOldCustomCover();
          setCoverBackground({ url: defaultCoverImage.value });
          break;
        case "Go to Workspace":
          router.replace({ name: "workspace" });
          break;
      }
    }
    async function secondBtnHandler() {
      if (coverImgButtons.secondBtn === "Go to Project") {
        router.replace({
          name: "project-detail",
          params: { projectId: savedProjectId.value }
        });
        return;
      }
      if (steps.projectInfo === 0) {
        return;
      } else if (steps.projectImages === 0) {
        afterImagesUploaded(true);
      } else if (steps.clientInfo === 0) {
        afterClientInfoSaved(true);
      } else if (coverImgButtons.secondBtn === "Done") {
        showSpinner.value = true;
        await store.dispatch("updateProject", {
          updateData: { coverUrl: coverBackground.downloadUrl },
          projectId: savedProjectId.value
        });
        showSpinner.value = false;
        afterDone();
      }
    }
    function afterDone() {
      actions.changeCoverImage = false;
      setCoverImgStyle("large", "full");
      setCoverImgContent(
        savedProject.value.name,
        "Has been successfully created!",
        "pi-check-circle"
      );
      setCoverImgButtons("Go to Workspace", "Go to Project");
    }

    /* RESPONSIVE */
    const isFocused = ref(false);
    const prevHeight = ref(null);
    const newHeight = ref(null);

    onMounted(() => {
      formResize();
    });

    function formResize() {
      prevHeight.value = window.innerHeight;
      const inputs = document.getElementsByTagName("input");
      const textArea = document.getElementById("notes");
      window.addEventListener("resize", ev => {
        if (newHeight.value) {
          prevHeight.value = newHeight.value;
        }
        newHeight.value = ev.target.innerHeight;
        if (checkMobile()) {
          if (isFocused.value === true && newHeight.value > prevHeight.value) {
            setCoverImgStyle("small", null, null);
            inputs.forEach(input => {
              input.blur();
            });
            textArea.blur();
          }
        }
      });

      inputs.forEach(input => {
        input.addEventListener("focus", ev => {
          inputEvent(ev);
        });
        input.addEventListener("blur", ev => {
          inputEvent(ev);
        });
      });

      textArea.addEventListener("focus", ev => {
        inputEvent(ev);
      });
      textArea.addEventListener("blur", ev => {
        inputEvent(ev);
      });
    }

    function inputEvent(ev) {
      if (checkMobile()) {
        if (ev.type === "focus") {
          isFocused.value = true;
          setCoverImgStyle("keyboard", null, null);
        } else if (ev.type === "blur") {
          isFocused.value = false;
          setCoverImgStyle("small", null, null);
        }
      }
    }

    function checkMobile() {
      const mobileBar = document.getElementById("mobile-bar");
      const style = window.getComputedStyle(mobileBar);
      const display = style.getPropertyValue("display");
      return display === "block" ? true : false;
    }

    return {
      showContent,
      steps,
      content,
      showSpinner,
      uploadImages,
      saveProjectForm,
      saveClientForm,
      actions,
      addImages,
      clearUpload,
      imagesSelected,
      coverImgButtons,
      coverImgStyle,
      coverImgContent,
      firstBtnHandler,
      secondBtnHandler,
      coverBackground,
      setCoverBackground,
      saveCustomCover,
      imageUrls
    };
  }
};
</script>

<style lang="scss" scoped>
body {
  overflow: hidden;
}

.container {
  margin: 0 auto 2rem auto;
  max-width: 40rem;
  border-radius: 0 0 12px 12px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  background-color: #ffffff;
}

.project-container {
  height: 60vh;
  padding: 2rem 1rem 2rem 2rem;
  overflow-y: scroll;
  transition: all $animation-slow;
}

.client-container {
  height: 60vh;
  padding: 2rem 1rem 2rem 2rem;
  overflow-y: scroll;
  transition: all $animation-slow;
}

.images-container {
  height: 28vh;
  padding: 2rem;
  overflow-y: scroll;
  transition: all $animation-slow;
}

.change-cover-container {
  height: 45vh;
  padding: 2rem;
  overflow-y: scroll;
  transition: all $animation-slow;
}

.images-chosen {
  height: 60vh;
  overflow-y: scroll;
  transition: all $animation-slow;
}

form {
  display: flex;
  flex-direction: column;

  .p-field:last-child {
    margin-bottom: 0;
  }
}

/* ANIMATIONS */
@keyframes form {
  from {
    height: 0vh;
    padding: 0rem 1rem 0rem 2rem;
  }
}

@keyframes images {
  from {
    height: 0vh;
    padding: 0 2rem;
  }
}

.project-leave-active,
.client-leave-active {
  animation: form $animation-slow linear reverse;
}

.images-enter-active {
  animation: images $animation-slow linear;
}

.images-leave-active {
  animation: images $animation-slow linear reverse;
}

.client-enter-active {
  animation: form $animation-slow linear;
}

.change-cover-enter-active {
  animation: images $animation-slow linear;
}

.change-cover-leave-active {
  animation: images $animation-slow linear reverse;
}

@media only screen and (max-width: $screen-small) {
  .container {
    border-radius: 0;
    box-shadow: none;
  }

  .project-container {
    height: auto;
    border-radius: 0;
    padding: 2rem 1rem 2rem 2rem;
  }

  .client-container {
    height: auto;
    border-radius: 0;
    padding: 2rem 1rem 2rem 2rem;
  }

  .images-container {
    height: calc(70vh - (#{$mobile-bar} / 2));
    padding: 2rem;
  }

  .change-cover-container {
    height: calc(70vh - (#{$mobile-bar} / 2));
    padding: 2rem;
  }

  .images-chosen {
    height: calc(100vh - #{$cover-img-mobile-md} - #{$mobile-bar});
    transition: all $animation-slow-mobile;
  }

  form {
    margin-right: 1rem;
  }

  /* ANIMATIONS */
  @keyframes form-mobile {
    from {
      height: 0px;
      padding: 0rem 1rem 0rem 2rem;
    }
    to {
      height: calc(100vh - #{$cover-img-mobile-md} - #{$mobile-bar});
    }
  }

  @keyframes images-mobile {
    from {
      height: 0px;
      padding: 0 2rem;
    }
  }

  .project-leave-active,
  .client-leave-active {
    animation: form-mobile $animation-slow-mobile linear reverse forwards;
  }

  .client-enter-active {
    animation: form-mobile $animation-slow-mobile linear forwards;
  }

  .images-enter-active {
    animation: images-mobile $animation-slow-mobile linear;
  }

  .images-leave-active {
    animation: images-mobile $animation-slow-mobile linear reverse;
  }

  .change-cover-enter-active {
    animation: images-mobile $animation-slow-mobile linear;
  }

  .change-cover-leave-active {
    animation: images-mobile $animation-slow-mobile linear reverse;
  }
}
</style>
